// File: Components/Structural/Sidebar/index.js

import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import MobileSidebarButton from "./MobileSidebarButton";
import routesConfig, { getTranslation } from "../../../Config/sidebarConfig.js";
import configTranslations from "../../../Config/translations.json";
import {
  FaChevronRight,
  FaChevronLeft,
  FaChevronDown,
  FaChevronUp,
  FaCog,
  FaQuestionCircle,
  FaGem,
  FaEye,
  FaCalendarAlt,
  FaGift,
  FaUserTie,
  FaRocket,
  FaEnvelope,
  FaGraduationCap,
  FaUser,
  FaPaperPlane,
  FaCrown,
  FaStar,
  FaBullhorn,
  FaInfoCircle,
} from "react-icons/fa";
import useApi from "../../../Hooks/useApi";
import useTranslations from "../../../Hooks/useTranslations";
import redirectToAppWithSession from "./utils/appRedirect";
import "./css/sidebar.css";
import "./css/mobile.css";

import {
  appsConfig,
  shouldShowAppsSection,
  toolsConfig,
} from "./sidebarAppsConfig";
import translations from "./translations.json";
import { FaGear, FaGears } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { selectEmailInboxEnabled, selectNewsletterEnabled } from "../../../store/setupApi";

const Sidebar = ({ onToggleExpand }) => {
  // Use reactive translations that update when language changes
  const t = useTranslations(translations);
  const configT = useTranslations(configTranslations);

  // Helper to get route label from translation key
  const getRouteLabel = (route) => {
    if (route.labelKey) {
      return getTranslation(configT, route.labelKey, route.labelKey);
    }
    return route.label || route.path;
  };

  const emailInboxEnabled = useSelector(selectEmailInboxEnabled);
  const newsletterEnabled = useSelector(selectNewsletterEnabled);
  const isIframe = typeof window !== "undefined" && window.isIframe;
  const [activeTab, setActiveTab] = useState(routesConfig[0].path);
  const [isExpanded, setIsExpanded] = useState(true);
  const [collapseTimeout, setCollapseTimeout] = useState(null);
  const [installedApps, setInstalledApps] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileSidebarVisible, setIsMobileSidebarVisible] = useState(false);
  const [tutorialActive, setTutorialActive] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null); // Track which section is expanded
  const [giftSendingVisible, setGiftSendingVisible] = useState(
    () => localStorage.getItem("giftSendingCompleted") !== "true"
  );

  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect mobile layout
  useEffect(() => {
    const checkMobile = () => {
      const m = window.innerWidth < 900;
      setIsMobile(m);
      if (m) setIsExpanded(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Load installed apps from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("installedApps");
    if (stored) setInstalledApps(JSON.parse(stored));
  }, []);

  // Listen for gift sending visibility events
  useEffect(() => {
    const handleGiftSendingCompleted = () => {
      setGiftSendingVisible(false);
    };
    const handleGiftSendingVisible = () => {
      setGiftSendingVisible(true);
    };
    window.addEventListener("giftSendingCompleted", handleGiftSendingCompleted);
    window.addEventListener("giftSendingVisible", handleGiftSendingVisible);
    return () => {
      window.removeEventListener("giftSendingCompleted", handleGiftSendingCompleted);
      window.removeEventListener("giftSendingVisible", handleGiftSendingVisible);
    };
  }, []);

  // Set active tab based on current location
  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  const colors = [
    "#48aaaf",
    "#4bb0b5",
    "#4fb5ba",
    "#53bbbf",
    "#56c0c4",
    "#5ac6c9",
    "#5ecbce",
    "#62d1d3",
    "#66d6d8",
    "#69dcdd",
  ];

  const collapseIfMobile = () => {
    if (isExpanded && isMobile && !tutorialActive) {
      setIsExpanded(false);
      setIsMobileSidebarVisible(false);
    }
  };

  const handleItemClick = (path) => {
    // Find the route configuration
    const route = routesConfig.find((r) => r.path === path);

    // Handle external app routes with JWT authentication
    if (route && route.isExternalApp) {
      setActiveTab(path);
      redirectToAppWithSession(api, route.appUrl, route.appDomain);
      collapseIfMobile();
      return;
    }

    // Handle stripe dashboard
    if (path === "/stripe-dashboard") {
      return handleStripeDashboard(path);
    }

    // Handle normal routes
    setActiveTab(path);
    navigate(path);
    collapseIfMobile();
    collapseTimeout && clearTimeout(collapseTimeout);
  };

  const handleStripeDashboard = async (path) => {
    try {
      setActiveTab(path);
      const { dashboardUrl } = await api.get(
        `${window.baseDomain}api/account-link`
      );
      if (dashboardUrl) window.open(dashboardUrl, "_blank");
      else navigate(path);
      collapseIfMobile();
    } catch {
      navigate(path);
    }
  };

  const handleAppClick = (app) => {
    if (app.domain) redirectToAppWithSession(api, app.url, app.domain);
    else window.location.href = app.url;
    collapseIfMobile();
  };

  const handleToolClick = (tool) => {
    if (tool.domain) redirectToAppWithSession(api, tool.url, tool.domain);
    else window.open(tool.url, "_blank");
    collapseIfMobile();
  };


  // Generic handler for section toggling - ensures only one section is open at a time
  const handleSectionToggle = (sectionName) => {
    setExpandedSection((prevExpanded) =>
      prevExpanded === sectionName ? null : sectionName
    );
  };

  const toggleSidebar = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // When collapsing sidebar, also collapse all subcategories
    if (!newExpandedState) {
      setExpandedSection(null);
    }

    if (isMobile) setIsMobileSidebarVisible((v) => !v);
    collapseTimeout && clearTimeout(collapseTimeout);
  };

  const handleMouseLeave = () => {
    if (isExpanded && !tutorialActive) {
      const tOut = setTimeout(() => {
        setIsExpanded(false);
        // Also collapse all subcategories when auto-collapsing
        setExpandedSection(null);
      }, 5000);
      setCollapseTimeout(tOut);
    }
  };

  const handleMouseEnter = () =>
    collapseTimeout && clearTimeout(collapseTimeout);

  useEffect(
    () => () => collapseTimeout && clearTimeout(collapseTimeout),
    [collapseTimeout]
  );

  // Adjust padding for content when sidebar expands/collapses
  useEffect(() => {
    const els = document.querySelectorAll(".withHeader");
    els.forEach((el) => {
      el.style.paddingLeft = isExpanded ? "200px" : "60px";
    });
    return () => {
      els.forEach((el) => {
        el.style.paddingLeft = "";
      });
    };
  }, [isExpanded, activeTab, location.pathname]);

  useEffect(() => onToggleExpand?.(isExpanded), [isExpanded, onToggleExpand]);

  // Listen for tutorial events to auto-expand sidebar sections
  useEffect(() => {
    const handleTutorialExpand = (e) => {
      const { section } = e.detail || {};
      setIsExpanded(true);
      setExpandedSection(section || null);
      setTutorialActive(true);
    };
    const handleTutorialEnd = () => {
      setTutorialActive(false);
    };
    window.addEventListener('tutorial:expandSection', handleTutorialExpand);
    window.addEventListener('tutorial:end', handleTutorialEnd);
    return () => {
      window.removeEventListener('tutorial:expandSection', handleTutorialExpand);
      window.removeEventListener('tutorial:end', handleTutorialEnd);
    };
  }, []);

  // Click-outside to close sidebar on mobile
  useEffect(() => {
    const outside = (e) => {
      if (
        isMobile &&
        isExpanded &&
        !tutorialActive &&
        !e.target.closest(".sidebar-component") &&
        !e.target.closest(".mobile-sidebar-toggle")
      ) {
        setIsExpanded(false);
        setIsMobileSidebarVisible(false);
        setExpandedSection(null); // Also collapse subcategories
      }
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [isMobile, isExpanded, tutorialActive]);

  if (isIframe) return null;

  // Hide sidebar on mobile — bottom nav replaces it
  if (isMobile) return null;

  // Get all menu routes first
  const allMenuRoutes = routesConfig.filter(
    (r) => r.isMenu && r.path !== "/restaurant-page"
  );

  // Define the Reservaties subsections
  const reservatiesSubsections = {
    dashboard: {
      title: null, // No title for standalone Dashboard
      routes: allMenuRoutes.filter((r) => r.path === "/"),
    },
    starten: {
      title: t.subsections?.starten || "Starten",
      routes: allMenuRoutes.filter(
        (r) =>
          r.path === "/uitzonderingen" ||
          r.path === "/openinghours/dinner" ||
          r.path === "/mateza-cloud-apps"
      ),
    },
    essentieel: {
      title: t.subsections?.essentieel || "Essentieel",
      routes: allMenuRoutes.filter(
        (r) =>
          r.path === "/tafelplan" ||
          r.path === "/mededelingen" ||
          r.path === "/menu" ||
          r.path === "/account/team"
      ),
    },
    geavanceerd: {
      title: t.subsections?.geavanceerd || "Geavanceerd",
      routes: allMenuRoutes.filter(
        (r) => r.path === "/max-groups" || r.path === "/max-arrivals"
      ),
    },
  };

  // Cadeaubon section: order explicitly between "Uw Winst" (stripe) and "Cadeaubon"
  const cadeaubonnenRoutes = [
    allMenuRoutes.find((r) => r.path === "/stripe-dashboard"),
    allMenuRoutes.find((r) => r.path === "/cadeaubonnen/banners"), // <-- Ontwerp / Design / Conception
    allMenuRoutes.find((r) => r.path === "/cadeaubonnen"),
    allMenuRoutes.find((r) => r.path === "/cadeaubonnen/activity"),
  ].filter(Boolean);

  const contactsRoutes = [
    allMenuRoutes.find((r) => r.path === "/contacts"),
    allMenuRoutes.find((r) => r.path === "/review"),
    allMenuRoutes.find((r) => r.path === "/gallery"),
  ].filter(Boolean);

  const emailRoutes = [
    allMenuRoutes.find((r) => r.path === "/email"),
    allMenuRoutes.find((r) => r.path === "/email/extension"),
    newsletterEnabled ? allMenuRoutes.find((r) => r.path === "/email/newsletter") : null,
    emailInboxEnabled ? allMenuRoutes.find((r) => r.path === "/inbox") : null,
  ].filter(Boolean);

  const appsToShow = appsConfig.filter(
    (a) => a.alwaysShow || installedApps[a.id]
  );
  const showApps = shouldShowAppsSection(installedApps) || true;

  const mobileVariants = {
    open: {
      x: 0,
      boxShadow: "5px 0 15px rgba(0,0,0,0.2)",
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
    closed: {
      x: "-100%",
      boxShadow: "none",
      transition: { type: "spring", stiffness: 300, damping: 30 },
    },
  };

  // Helper function to check if a route is active
  const isRouteActive = (routePath) => {
    return activeTab === routePath;
  };

  const renderExpandableSection = (
    sectionKey,
    sectionTitle,
    sectionIcon,
    routes,
    colorIndex,
    emptyMessage = null
  ) => {
    const isSectionExpanded = expandedSection === sectionKey;

    return (
      <div className="sidebar-item-container" key={sectionKey}>
        <SidebarItem
          item={{
            id: sectionKey,
            title: sectionTitle,
            icon: sectionIcon,
            isExpandable: true,
            isExpanded: isSectionExpanded,
          }}
          activeTab={activeTab}
          handleItemClick={() => {
            if (isExpanded) {
              // Sidebar is already expanded, just toggle the section
              handleSectionToggle(sectionKey);
            } else {
              // Sidebar is contracted, expand it and show this section
              setIsExpanded(true);
              setExpandedSection(sectionKey);
              if (isMobile) setIsMobileSidebarVisible(true);
            }
          }}
          isExpanded={isExpanded}
          activeColor={colors[colorIndex]}
        />

        {/* Section Subitems */}
        <motion.div
          initial={false}
          animate={{
            height: isExpanded && isSectionExpanded ? "auto" : 0,
            opacity: isExpanded && isSectionExpanded ? 1 : 0,
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0.0, 0.2, 1],
          }}
          style={{ overflow: "hidden" }}
        >
          {isExpanded && (
            <div className="sidebar-item__secondary">
              {routes.length > 0
                ? routes.map((route, index) => (
                    <motion.div
                      key={route.path}
                      data-tutorial-target={route.path}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{
                        opacity: isSectionExpanded ? 1 : 0,
                        x: isSectionExpanded ? 0 : -10,
                      }}
                      transition={{
                        duration: 0.2,
                        delay: isSectionExpanded ? index * 0.05 : 0,
                        ease: [0.4, 0.0, 0.2, 1],
                      }}
                      className={`sidebar-item__secondary-item ${
                        isRouteActive(route.path)
                          ? "sidebar-item__secondary-active"
                          : ""
                      }`}
                      onClick={() => handleItemClick(route.path)}
                    >
                      <div className="sidebar-secondary-content">
                        <span className="sidebar-secondary-text">
                          {getRouteLabel(route)}
                        </span>
                      </div>
                    </motion.div>
                  ))
                : emptyMessage && (
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{
                        opacity: isSectionExpanded ? 1 : 0,
                        x: isSectionExpanded ? 0 : -10,
                      }}
                      transition={{
                        duration: 0.2,
                        ease: [0.4, 0.0, 0.2, 1],
                      }}
                      className="sidebar-item__secondary-item sidebar-empty-message"
                    >
                      <div className="sidebar-secondary-content">
                        <span className="sidebar-secondary-text">
                          {emptyMessage}
                        </span>
                      </div>
                    </motion.div>
                  )}
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  // Special render function for Reservaties section with subsections
  const renderReservatieSectionWithSubsections = () => {
    const sectionKey = "reservaties-main";
    const isSectionExpanded = expandedSection === sectionKey;

    return (
      <div className="sidebar-item-container" key={sectionKey}>
        <SidebarItem
          item={{
            id: sectionKey,
            title: t.sidebar.reservations,
            icon: FaCalendarAlt,
            isExpandable: true,
            isExpanded: isSectionExpanded,
          }}
          activeTab={activeTab}
          handleItemClick={() => {
            if (isExpanded) {
              handleSectionToggle(sectionKey);
            } else {
              setIsExpanded(true);
              setExpandedSection(sectionKey);
              if (isMobile) setIsMobileSidebarVisible(true);
            }
          }}
          isExpanded={isExpanded}
          activeColor={colors[0]}
        />

        {/* Reservaties Subitems with Subsections */}
        <motion.div
          initial={false}
          animate={{
            height: isExpanded && isSectionExpanded ? "auto" : 0,
            opacity: isExpanded && isSectionExpanded ? 1 : 0,
          }}
          transition={{
            duration: 0.3,
            ease: [0.4, 0.0, 0.2, 1],
          }}
          style={{ overflow: "hidden" }}
        >
          {isExpanded && (
            <div className="sidebar-item__secondary">
              {Object.entries(reservatiesSubsections).map(
                ([subsectionKey, subsection], subsectionIndex) => (
                  <div key={subsectionKey}>
                    {/* Subsection Title (if exists) */}
                    {subsection.title && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{
                          opacity: isSectionExpanded ? 1 : 0,
                          x: isSectionExpanded ? 0 : -10,
                        }}
                        transition={{
                          duration: 0.2,
                          delay: isSectionExpanded ? subsectionIndex * 0.1 : 0,
                          ease: [0.4, 0.0, 0.2, 1],
                        }}
                        className="sidebar-subsection-title"
                      >
                        {subsection.title}
                      </motion.div>
                    )}

                    {/* Subsection Routes */}
                    {subsection.routes.map((route, routeIndex) => (
                      <motion.div
                        key={route.path}
                        data-tutorial-target={route.path}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{
                          opacity: isSectionExpanded ? 1 : 0,
                          x: isSectionExpanded ? 0 : -10,
                        }}
                        transition={{
                          duration: 0.2,
                          delay: isSectionExpanded
                            ? subsectionIndex * 0.1 + routeIndex * 0.05
                            : 0,
                          ease: [0.4, 0.0, 0.2, 1],
                        }}
                        className={`sidebar-item__secondary-item ${
                          isRouteActive(route.path)
                            ? "sidebar-item__secondary-active"
                            : ""
                        }`}
                        onClick={() => handleItemClick(route.path)}
                      >
                        <div className="sidebar-secondary-content">
                          <span className="sidebar-secondary-text">
                            {getRouteLabel(route)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}
        </motion.div>
      </div>
    );
  };

  return (
    <>
      {/* Extension content (no route needed) */}

      <motion.div
        className={`sidebar-component ${isExpanded ? "expanded" : ""}`}
        layout
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        animate={isMobile ? (isMobileSidebarVisible ? "open" : "closed") : {}}
        variants={isMobile ? mobileVariants : {}}
        initial={isMobile ? "closed" : false}
      >
        {/* Main scrollable content */}
        <div className="sidebar-main-content">
          {/* Reservaties Section with Subsections */}
          {renderReservatieSectionWithSubsections()}

          {/* Cadeaubon Section */}
          {renderExpandableSection(
            "cadeaubonnen-main",
            t.sidebar.giftCards,
            FaGift,
            cadeaubonnenRoutes,
            1
          )}

          {/* Contacts Section - Now expandable with Reviews */}
          {renderExpandableSection(
            "contacts-main",
            t.sidebar.contacts,
            FaUser,
            contactsRoutes,
            6
          )}

          {/* Email Section */}
          {renderExpandableSection(
            "email-main",
            t.apps.email,
            FaEnvelope,
            emailRoutes,
            2
          )}

          {/* About Section (Knowledge Base) */}
          {emailInboxEnabled && (
            <SidebarItem
              item={{
                id: "/about",
                title: t.sidebar.about || "About",
                icon: FaInfoCircle,
              }}
              activeTab={activeTab}
              handleItemClick={() => handleItemClick("/about")}
              isExpanded={isExpanded}
              activeColor={colors[5]}
            />
          )}

          {/* Marketing Section */}
          <SidebarItem
            item={{
              id: "/marketing",
              title: t.sidebar.marketing || "Marketing",
              icon: FaBullhorn,
            }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/marketing")}
            isExpanded={isExpanded}
            activeColor={colors[3]}
          />

          {/* Subscription Section - Primary standalone item */}
          <SidebarItem
            item={{
              id: "/subscription",
              title: t.sidebar.subscription,
              icon: FaStar,
            }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/subscription")}
            isExpanded={isExpanded}
            activeColor={colors[7]}
          />

          <SidebarItem
            item={{
              id: "/restaurant-page",
              title: t.sidebar.website,
              icon: FaGem,
            }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/restaurant-page")}
            isExpanded={isExpanded}
            activeColor={colors[3]}
          />

          {/* Training Section */}
          <SidebarItem
            item={{
              id: "/training",
              title: t.sidebar.training,
              icon: FaGraduationCap,
            }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/training")}
            isExpanded={isExpanded}
            activeColor={colors[4]}
          />

        </div>

        {/* Bottom section - Fixed at bottom */}
        <div className="sidebar-bottom-section">
          <SidebarItem
            item={{ id: "/settings", title: t.sidebar.settings, icon: FaGear }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/settings")}
            isExpanded={isExpanded}
            activeColor={colors[1]}
          />
          {/* Application page (app, tokens, MFA) */}
          <SidebarItem
            item={{
              id: "/security",
              title: t.sidebar.security,
              icon: FaRocket,
            }}
            activeTab={activeTab}
            handleItemClick={() => handleItemClick("/security")}
            isExpanded={isExpanded}
            activeColor={colors[5]}
          />

          {toolsConfig.map((tool) => (
            <SidebarItem
              key={tool.id}
              item={{
                id: tool.path,
                title: tool.title,
                icon: tool.icon,
                docLink: tool.docLink,
              }}
              activeTab={activeTab}
              handleItemClick={() => handleToolClick(tool)}
              isExpanded={isExpanded}
              activeColor={tool.activeColor || colors[8]}
            />
          ))}
        </div>

        <div className="sidebar-controls">
          <div className="sidebar-toggle-group">
            <div className="sidebar-toggle" onClick={toggleSidebar}>
              {isExpanded ? <FaChevronLeft /> : <FaChevronRight />}
            </div>
          </div>
        </div>
      </motion.div>

      {isMobile && (
        <MobileSidebarButton
          onClick={() => {
            setIsExpanded(true);
            setIsMobileSidebarVisible(true);
          }}
          isVisible={!isMobileSidebarVisible}
        />
      )}
    </>
  );
};

export default Sidebar;